import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { Observable, Subject, catchError, debounceTime, distinctUntilChanged, filter, map, of, share, skip, switchMap, tap } from 'rxjs';
import { SearchSelectService } from './search-select.service';
import { trigger, state, style, animate, transition } from '@angular/animations';
import { MatDialog } from '@angular/material/dialog';
import { DeviceDetectorService } from 'ngx-device-detector';
import { MobileSelectionComponent } from './mobile-selection/mobile-selection.component';
import { Language } from '../modals/edit-about/language/language.component';
import { OverlayContainer } from '@angular/cdk/overlay';

const widthAnimation = trigger('widthAnimation', [
  state('*', style({
    width: '*',
    overflow: 'hidden'
  })),
  transition(':leave', [
    style({ width: '*', overflow: 'hidden' }),
    animate('200ms ease-in-out', style({ width: '0px', overflow: 'hidden' }))
  ])
]);

export type ItemType = {
  id: number;
  name: string;
  province?: string;
  country?: string;
  username?: string;
  logo: string;
  native_name?: string;
}

@Component({
  selector: 'app-search-select',
  templateUrl: './search-select.component.html',
  styleUrls: ['./search-select.component.scss'],
  animations: [widthAnimation]
})
export class SearchSelectComponent {
  selectedIndex = -1
  @ViewChild('listContainer') listContainer!: ElementRef;
  @Input() config: { rounded: boolean, roundedFull?: boolean,clearAfter?:boolean } = { rounded: false, roundedFull: false, clearAfter:false }
  @Input() layer: number = 0;
  @Input() initSelectItem: { id: number, name: string } | ItemType | Language | null | undefined;
  @Input() type: 'school' | 'city' | 'language' | 'software' | 'skill' | 'field' | 'organ' | 'work_field' = 'city';
  @Input() isCheck: boolean = false;
  @Input() invalidClass: any
  @Input() specificHeight: any
  @Input() nullPossible: any = false
  @Input() optionalField: any = false
  @Output() emitId: EventEmitter<ItemType | null> = new EventEmitter();
  @Output() emitCustom: EventEmitter<string> = new EventEmitter();
  @Output() emitCreateObs: EventEmitter<Observable<any> | null> = new EventEmitter();
  @ViewChild('searchSelectInput') input: ElementRef | undefined
  nextPageUrl: string | null = null;
  items: Subject<ItemType[] | null> = new Subject();
  itemsObs: Observable<ItemType[] | null> = this.items.asObservable().pipe(
    share(),
  );
  focusSubject: Subject<null | string> = new Subject();
  focusObs: Observable<null | string> = this.focusSubject.asObservable();
  inputName: string | null = null;
  selectedItem: any | ItemType | null = null;
  dontCheckInput: string | null = null;
  cloneItem: any;
  canCreate: boolean = false;
  focusInput = false;
  active = -1
  constructor(
    private formBuilder: UntypedFormBuilder,
    private searchSelect: SearchSelectService,
    private dialog: MatDialog,
    private device: DeviceDetectorService,
    private overlayContainer: OverlayContainer,
  ) { }
  formGroup = this.formBuilder.group({
    control: new UntypedFormControl(),
  });
  ngOnInit(): void {
    this.searchSelect.searchSelectOpenedSignal.set(false)
    this.cloneItem = Object.assign({}, this.initSelectItem)
    this.getItemsObs().subscribe()
    this.initialSelectedItem()
    document.addEventListener('keydown', (e) => {
      const dropdownMenu = document.querySelector('.dropdown-menu');
      const dropdownChildren = dropdownMenu?.children ?? [];
      
      // Initialize active if not set
      if (this.active === undefined) {
        this.active = -1;
      }
    
      if (!dropdownChildren.length) return; // Exit if no items
    
      if (e.key === "ArrowUp") { // Arrow Up
        e.preventDefault();
        if (this.active > 0) {
          // Remove focus from all items first
          Array.from(dropdownChildren).forEach(child => child.classList.remove('focus'));
          
          this.active--;
          dropdownChildren[this.active]?.classList.add('focus');
          (dropdownChildren[this.active] as HTMLElement).focus();
        } else if (this.active === -1) {
          // Start from bottom if nothing was selected
          this.active = dropdownChildren.length - 1;
          dropdownChildren[this.active]?.classList.add('focus');
          (dropdownChildren[this.active] as HTMLElement).focus();
        }
      } else if (e.key === "ArrowDown") { // Arrow Down
        e.preventDefault();
        if (this.active < dropdownChildren.length - 1) {
          // Remove focus from all items first
          Array.from(dropdownChildren).forEach(child => child.classList.remove('focus'));
          
          this.active++;
          dropdownChildren[this.active]?.classList.add('focus');
          (dropdownChildren[this.active] as HTMLElement).focus();
        } else if (this.active === -1) {
          // Start from top if nothing was selected
          this.active = 0;
          dropdownChildren[this.active]?.classList.add('focus');
          (dropdownChildren[this.active] as HTMLElement).focus();
        }
      } else if (e.key === "Enter") { // Enter key
        e.preventDefault();
        if (this.active !== -1) {
          // Simulate a click on the active item
          (dropdownChildren[this.active] as HTMLElement)?.click();
          // Reset active after selection
          this.active = -1;
          // Remove focus from all items
          Array.from(dropdownChildren).forEach(child => child.classList.remove('focus'));
        }
      }
    });
    
  }
  initialSelectedItem(): void {
    // console.log(this.initSelectItem)
    if (this.initSelectItem && this.initSelectItem.name) {
      this.selectedItem = this.initSelectItem
      this.emitId.emit(this.selectedItem)
      this.formGroup.controls['control'].setValue(
        this.type == 'language' ? `${this.selectedItem?.name} (${this.selectedItem?.native_name})` : this.selectedItem?.name
      )
    }
  }
  showError(): boolean {
    let val: any = this.formGroup.controls['control'].value
    // console.log(!this.selectedItem , !this.canCreate)
    return this.isCheck && (val == '' || val == null || val == undefined) || (val != this.initSelectItem?.name && (!this.canCreateString() && !this.selectedItem))
  }
  isOk(): boolean {
    let val: any = this.formGroup.controls['control'].value
    return !this.isCheck || this.selectedItem != null || this.canCreateString() && (val && val != '' && val != null)
  }
  getIsShowOnBottom(): boolean {
    let child = this.input?.nativeElement.getBoundingClientRect();
    let overlayElement = this.overlayContainer.getContainerElement();
    let isOvelayElement = overlayElement.querySelector('#search-select-parent')
    let doc = isOvelayElement ? overlayElement.querySelector('#search-select-parent') : document.querySelector('#search-select-parent');
    let parent = doc?.getBoundingClientRect();
    return parent!.bottom > child.bottom + (isOvelayElement ? 150 : 0);
  }
  isMobile(): boolean { return this.device.isMobile() }
  getInputValue(): Observable<string> {
    return this.formGroup.controls['control'].valueChanges.pipe(
      skip(!this.initSelectItem ? 1 : 2),
      tap(i => {
        this.selectedItem = null;
        this.emitId.emit(null)
        this.inputName = i;
      }),
      filter((s: string) => {
        if (!this.dontCheckInput || this.dontCheckInput != s)
          return true
        else
          this.dontCheckInput = null
        return false
      }),
      distinctUntilChanged(),
      debounceTime(300),
    )
  }
  getItemsObs(): Observable<any> {
    return this.getInputValue().pipe(
      switchMap((text) => this.searchSelect.getItems(this.type, text).pipe(
        map((result: any) => {
          this.active = -1
          this.searchSelect.searchSelectOpenedSignal.set(true)
          this.items.next(result.results)
          this.nextPageUrl = result.next
          let index: number = result.results.findIndex((r: any) => r.name == text)
          this.canCreate = this.canCreateString() && index <= -1
          if (index <= -1)
            if (this.canCreateString())
              this.createString()
            else
              this.selectedItem = null
          else
            this.selectItem(result.results[index], false)
        }),
        catchError(err => {
          this.items.next([]); 
          return of(null); 
        })
      )),
    )
  }
  selectItem(item: ItemType, close: boolean = true) {
    this.formGroup.controls['control'].setValue(item.name, { emitEvent: false })
    this.selectedItem = item;
    this.emitId.emit(item);
    if(this.config.clearAfter){this.formGroup.controls['control'].setValue('', { emitEvent: false })}
    if (close)
    this.items.next(null)
    setTimeout(() => {
      this.searchSelect.searchSelectOpenedSignal.set(false)
      // console.log(this.searchSelect.searchSelectOpenedSignal())
    }, 100);
    this.active = -1
  }
  isNotExist(itemList: ItemType[] | null): boolean {
    if (this.inputName == null || itemList == null)
      return false
    return itemList.findIndex(element => element.name == this.inputName) <= -1
  }
  reset(): void {
    setTimeout(() => {
      this.searchSelect.searchSelectOpenedSignal.set(false)
    }, 500);
    this.items.next(null)
    this.active = -1
  }
  openMobileSelection(): void {
    this.dialog.open(MobileSelectionComponent, {
      disableClose: true,
      id: `mobile-selection-${this.layer}`,
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: this.isMobile() ? '100vh' : 'auto',
      data: {
        isMobile: this.isMobile(),
        type: this.type,
        selectedItem: this.selectedItem,
        layer: this.layer,
      }
    }).afterClosed().subscribe(
      item => {
        if(typeof item=='string'){
          this.formGroup.controls['control'].setValue(item, { emitEvent: false })
          this.selectedItem = {name: item};
          this.createString()
        }else{
          this.selectedItem = item;
          this.emitId.emit(item)
        }
      }
    )
  }
  resetSelectedItem(): void {
    this.selectedItem = null;
  }
  focus(e: any) {
    this.focusSubject.next(e.target.value)
    // this.searchSelect.searchSelectOpenedSignal.set(true)
  }
  changeFocus(focusInput: any) {
    focusInput ?? setTimeout(() => {
      this.searchSelect.searchSelectOpenedSignal.set(false)
    }, 500);
    this.focusInput = focusInput
  }
  canCreateString(): boolean {
    // return merge(
    //   this.itemsObs.pipe(
    //     map((results:any)=>{
    //       let value = this.formGroup.controls['control'].value
    //       console.log('dar meni', this.type=='organ' && value!='' && value!=undefined && results.findIndex((r:any)=>r==value)<=-1)
    //       console.log(this.type=='organ')
    //       console.log(value!='')
    //       console.log( value!=undefined )
    //       console.log(results.findIndex((r:any)=>r==value)<=-1)
    //       return this.type=='organ' && value!='' && value!=undefined && results.findIndex((r:any)=>r==value)<=-1
    //     })
    //   )
    // )
    return (this.type == 'school' || this.type == 'field' || this.type == 'organ' || this.type == 'work_field' || this.type == 'software' || this.type == 'skill')
  }
  createString() {
    // console.log('create string')
    let form: FormData = new FormData()
    switch (this.type) {
      case ('work_field'): {
        form.append('name', this.formGroup.controls['control'].value)
        if (this.formGroup.controls['control'].value && this.formGroup.controls['control'].value != ''){
          this.emitCreateObs.emit(this.formGroup.controls['control'].value)
          this.emitCustom.emit(this.formGroup.controls['control'].value)
        }
        else
          this.emitCreateObs.emit(null)
        break
      }
      case ('organ'): {
        form.append('name', this.formGroup.controls['control'].value)
        if (this.formGroup.controls['control'].value && this.formGroup.controls['control'].value != '') {
          this.emitCreateObs.emit(this.formGroup.controls['control'].value)
          this.emitCustom.emit(this.formGroup.controls['control'].value)
        }
        else
          this.emitCreateObs.emit(null)
        break
      }
      case ('software'): {
        form.append('name', this.formGroup.controls['control'].value)
        if (this.formGroup.controls['control'].value && this.formGroup.controls['control'].value != '') {
          this.emitCreateObs.emit(this.formGroup.controls['control'].value)
          this.emitCustom.emit(this.formGroup.controls['control'].value)
        }
        else
          this.emitCreateObs.emit(null)
        break
      }
      case ('skill'): {
        form.append('name', this.formGroup.controls['control'].value)
        if (this.formGroup.controls['control'].value && this.formGroup.controls['control'].value != '') {
          this.emitCreateObs.emit(this.formGroup.controls['control'].value)
          this.emitCustom.emit(this.formGroup.controls['control'].value)
        }
        else
          this.emitCreateObs.emit(null)
        break
      }
      case ('school'): {
        this.emitCustom.emit(this.formGroup.controls['control'].value)
        // this.items.next(null)
        this.canCreate = false
        // form.append('additional_data', JSON.stringify({company_name: this.formGroup.controls['control'].value}))
        break
      }
      case ('field'): {
        this.emitCustom.emit(this.formGroup.controls['control'].value)
        // this.items.next(null)
        this.canCreate = false
        // form.append('additional_data', JSON.stringify({company_name: this.formGroup.controls['control'].value}))
        break
      }
      default: {
        break;
      }
    }
  }
  manageStyleClass() {
    if (this.focusInput) {
      return 'focusInput'
    }
    else if (!this.focusInput && (this.invalidClass || !this.isOk())) {
      return 'invalidError'
    }
    else {
      return 'notFocusInput'
    }
  }
  ngOnDestroy(): void {
    setTimeout(() => {
      this.searchSelect.searchSelectOpenedSignal.set(false)
    }, 100);
  }
}
